import React, { useEffect, useState } from 'react';
import { DownOutlined, EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Switch, Table, Tag, Typography } from 'antd';
import { apiClient, apiErrorMessage } from '../../../utils';
import { Role, Role2, User } from '../../../models';
import moment from 'moment';
import { EditUserRolesPage, NewUserPage } from '.';
import { EntityChangeLog, LoginAttemptLog } from '../../reports';
import { TableNameColumn } from '../../../elements';

export function UsersPage() {
  const { Content } = Layout;
  const { Text, Link } = Typography;

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editRolesModalOpen, setEditRolesModalOpen] = useState<undefined | 'add' | 'del'>();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>();
  const [selectedUsers, setSelectedUsers] = useState<User[]>();
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<number>();
  const [loginAttemptLogModalOpen, setLoginAttemptLogModalOpen] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = () => {
    setLoading(true);
    apiClient
      .get('users?minimal=true')
      .then(response => {
        setUsers(response.data as User[]);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openAddModal = () => {
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
  };

  const openEditRolesModal = (action: 'add' | 'del') => {
    setEditRolesModalOpen(action);
  };

  const closeEditRolesModal = () => {
    setEditRolesModalOpen(undefined);
  };

  const openChangeLogModal = (id: number) => {
    setChangeLogModalOpen(id);
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const openLoginAttemptLogModal = () => {
    setLoginAttemptLogModalOpen(true);
  };

  const closeLoginAttemptLogModal = () => {
    setLoginAttemptLogModalOpen(false);
  };

  const onRefreshClick = () => {
    loadUsers();
  };

  const onUserAdded = () => {
    closeAddModal();
    loadUsers();
  };

  const onRolesUpdated = () => {
    closeEditRolesModal();
    loadUsers();
  };

  const deleteUser = (id: number) => {
    setLoading(true);
    apiClient
      .delete(`users/${id}`)
      .then(() => {
        loadUsers();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const confirmDeleteUser = (id: number) => {
    const onOk = () => deleteUser(id);
    Modal.confirm({
      title: 'Kullanıcı sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Kullanıcıyı silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const onTableActionClick = (e: any, id: number) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    } else if (e.key === 'del') {
      confirmDeleteUser(id);
    }
  };

  const onBulkActionClick = (e: any) => {
    if (e.key === 'addRoles') {
      openEditRolesModal('add');
    } else if (e.key === 'delRoles') {
      openEditRolesModal('del');
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: User[]) => {
      if (selectedRows.length) {
        setSelectedUsers(selectedRows);
      } else {
        setSelectedUsers(undefined);
      }
    },
  };

  const updateUserReceiveEmails = (id: number, receiveEmails: boolean) => {
    setLoading(true);
    apiClient
      .put(`users/${id}/email-notifications`, { receiveEmails })
      .then(() => {
        const newUsers = users?.map(user => (user.id === id ? { ...user, receiveEmails } : user));
        setUsers(newUsers);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const tableActions = (id: number) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      <Menu.Item key="del">
        <Text type="danger">Sil</Text>
      </Menu.Item>
    </Menu>
  );

  const bulkActionsMenu = (
    <Menu onClick={onBulkActionClick}>
      <Menu.Item key="addRoles">Rol ekle</Menu.Item>
      <Menu.Item key="delRoles">Rol sil</Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: 'İsim',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (name: string, user: User) => <TableNameColumn name={name} path={'/admin/users/' + user.id} />,
    },
    {
      title: 'No',
      dataIndex: 'employeeNumber',
      key: 'employeeNumber',
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => <Link href={'mailto:' + email}>{email}</Link>,
    },
    {
      title: 'Bölüm',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: 'Ünvan',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Roller',
      key: 'roles2',
      dataIndex: 'roles2',
      render: (roles2: Role2[], user: User) => (
        <>
          {!roles2?.length && !user.delegatedRoles?.length ? (
            '-'
          ) : (
            <>
              {roles2?.map(role => (
                <Tag key={role.id} color={role.name === 'Superadmin' ? 'red' : 'default'}>
                  {role.name}
                </Tag>
              ))}
              {user.delegatedRoles?.map(role => (
                <Tag key={role.id} color="blue">
                  {role.name}
                </Tag>
              ))}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Son giriş',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      render: (lastLoginAt: string) => (lastLoginAt ? moment(lastLoginAt).format('DD.MM.yyyy HH:mm:ss') : '-'),
    },
    {
      title: 'E-posta bildirimleri',
      dataIndex: 'receiveEmails',
      key: 'receiveEmails',
      render: (receiveEmails: boolean, user: User) => (
        <Switch checked={receiveEmails} onChange={() => updateUserReceiveEmails(user.id, !receiveEmails)} />
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, user: User) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(user.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <UserOutlined /> Kullanıcılar
          </>
        }
        extra={[
          (users?.length || 0) > 0 && <Text>Kullanıcı sayısı: {users?.length}</Text>,
          <Button key="attempt" onClick={openLoginAttemptLogModal} type="link">
            Yetkisiz erişimler
          </Button>,
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <Dropdown key="bulk" overlay={bulkActionsMenu}>
            <Button>
              Toplu işlemler <DownOutlined />
            </Button>
          </Dropdown>,
          <Button key="add" onClick={openAddModal} icon={<PlusOutlined />} type="primary">
            Yeni kullanıcı
          </Button>,
        ]}
      />
      <Content>
        <Table
          columns={tableColumns}
          rowKey={row => row.id}
          dataSource={users}
          rowSelection={{ ...rowSelection }}
          loading={loading}
          scroll={{ x: '100%' }}
          pagination={false}
        />
      </Content>
      <Modal title="Yeni kullanıcı" visible={addModalOpen} footer={null} onCancel={closeAddModal} destroyOnClose={true}>
        <NewUserPage onSaved={onUserAdded} />
      </Modal>
      <Modal
        title={'Toplu rol ' + (editRolesModalOpen === 'add' ? 'ekle' : 'sil')}
        visible={editRolesModalOpen !== undefined}
        footer={null}
        onCancel={closeEditRolesModal}
        destroyOnClose={true}
      >
        <EditUserRolesPage onSaved={onRolesUpdated} action={editRolesModalOpen} selectedUsers={selectedUsers as User[]} />
      </Modal>
      <Modal
        title="Kullanıcı değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'User'} recordId={changeLogModalOpen} />
      </Modal>
      <Modal
        title="Uygulamaya yetkisiz erişim denemeleri"
        visible={loginAttemptLogModalOpen}
        footer={null}
        onCancel={closeLoginAttemptLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <LoginAttemptLog />
      </Modal>
    </>
  );
}
